import { z } from 'zod'
import {
    sponsorBootstrapSchema, sponsorMeSchema, sponsorChildViewSchema, sponsorChildDetailSchema,
    sponsorOtpRequestResponseSchema, sponsorOtpVerifyResponseSchema, sponsorPreferencesViewSchema,
    sponsorAccountSchema, sponsorPrivacyRequestResponseSchema,
    sponsorNoticeListSchema, sponsorUpdatesPageSchema, sponsorThreadSummarySchema, sponsorThreadDetailSchema, sponsorThreadMessageSchema, sponsorHelpSchema,
    sponsorLoginResponseSchema, sponsorVerificationChallengeSchema, sponsorSignupVerifiedSchema, sponsorPasswordResetTokenSchema,
    sponsorDonationsSchema, sponsorReceiptSchema, sponsorDonationCatalogSchema, sponsorCheckoutResultSchema, sponsorSavedCardSchema,
    sponsorVisitRequestResponseSchema, sponsorImpactSchema, sponsorEventsSchema,
    SPONSOR_ERROR_CODES,
    type SponsorErrorCode, type SponsorOtpRequestInput, type SponsorPreferencesInput, type SponsorLoginInput,
    type SponsorSignupStartInput, type SponsorSignupCompleteInput, type SponsorPasswordForgotInput,
    type SponsorCheckoutInput, type SponsorVisitRequestInput, type SponsorProfileUpdate, type SponsorContactChangeStartInput,
    type SponsorOutgoingAttachments,
    sponsorPushConfigSchema, type SponsorPushSubscriptionInput,
    sponsorTranscriptionResultSchema,
    sponsorTranslationsSchema, type SponsorTranslateRequest,
    sponsorCatalogPageSchema, sponsorCatalogChildSchema, sponsorReservationSchema, sponsorshipResultSchema,
    type SponsorCatalogQuery, type SponsorshipCheckout,
    sponsorAppSponsorshipDetailSchema, sponsorAppCardChangeResultSchema,
    type SponsorAppCardChange, type SponsorAppPlanChange, type SponsorAppCancel,
    paymentsConfigSchema, sopTokenSchema,
    sponsorTimelinePageSchema, sponsorRitualListSchema,
    sponsorPostalCodeLookupSchema, type SponsorPasswordChangeInput,
} from '@/lib/contract'
import i18next from 'i18next'
import { platform } from '@/platform'
import { messagePayload } from './message-payload'
import { useTranslationsStore } from '@/stores/translations.store'

/* Spec PWA-001 — cliente HTTP do App do Padrinho (T057).
   Toda resposta e validada contra o schema compartilhado: campo fora do contrato quebra aqui, nao na tela.
   Em dev, sem a API do dominio sponsor (fases 3–5), as chamadas vao para o sandbox local (src/dev/sandbox/),
   que responde no mesmo envelope { data, error }. Em build de producao o sandbox nao existe. */

const BASE = '/api/v1/sponsor'

export type RawResponse = { status: number; body: unknown }
export type Transport = (method: string, path: string, body?: unknown) => Promise<RawResponse>

export class SponsorApiError extends Error {
    constructor(
        readonly code: SponsorErrorCode | 'network' | 'unknown',
        readonly status: number,
        readonly retryAt: string | null = null,
        /* Login: tentativas antes do bloqueio temporario (RN-05) */
        readonly attemptsLeft: number | null = null,
    ) {
        super(code)
        this.name = 'SponsorApiError'
    }
}

const errorEnvelopeSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string().optional(),
        retryAt: z.string().optional(),
        attemptsLeft: z.number().int().nonnegative().optional(),
    }).passthrough(),
}).passthrough()

export function toApiError(status: number, body: unknown): SponsorApiError {
    const parsed = errorEnvelopeSchema.safeParse(body)
    const code = parsed.success ? parsed.data.error.code : null
    const known = code && (SPONSOR_ERROR_CODES as readonly string[]).includes(code) ? code as SponsorErrorCode : null
    if (known && parsed.success) {
        return new SponsorApiError(known, status, parsed.data.error.retryAt ?? null, parsed.data.error.attemptsLeft ?? null)
    }
    if (status === 401) return new SponsorApiError('SPONSOR_SESSION_REQUIRED', status)
    if (status === 404) return new SponsorApiError('SPONSOR_NOT_FOUND', status)
    return new SponsorApiError('unknown', status)
}

const httpTransport: Transport = async (method, path, body) => {
    // PWA-013: mensagem com foto ou audio vai como FormData (o navegador define o Content-Type com o boundary)
    const form = body instanceof FormData
    const res = await fetch(`${BASE}${path}`, {
        method,
        credentials: 'include',
        headers: {
            'Accept': 'application/json',
            ...(body !== undefined && !form ? { 'Content-Type': 'application/json' } : {}),
            'X-Sponsor-Client': platform.client,
            'X-Sponsor-Display-Mode': platform.displayMode(),
            // PWA-016 D-03: idioma da tela, para os textos montados no servidor (Comunicados)
            'Accept-Language': i18next.language || 'pt-BR',
        },
        body: body === undefined ? undefined : form ? body : JSON.stringify(body),
    })
    const text = await res.text()
    return { status: res.status, body: text ? JSON.parse(text) as unknown : null }
}

let transportPromise: Promise<Transport> | null = null

function getTransport(): Promise<Transport> {
    if (!transportPromise) {
        // Condicao inline (sem variavel intermediaria) para o bundler eliminar o ramo — e o sandbox — no build.
        // VITE_SPONSOR_SANDBOX=true inclui o sandbox num build de demonstracao (homologacao sem API).
        // Spec PWA-002: VITE_SPONSOR_IMPERSONATION=true embrulha o sandbox no transporte hibrido (dados reais do CRM
        // nas rotas ja ligadas ao servidor). Mesma regra de condicao inline para o seletor sair do build sem a flag.
        transportPromise = import.meta.env.VITE_SPONSOR_SANDBOX === 'true' || (import.meta.env.DEV && import.meta.env.VITE_SPONSOR_DATA_SOURCE !== 'api')
            ? import('@/dev/sandbox').then(async m => import.meta.env.VITE_SPONSOR_IMPERSONATION === 'true'
                ? (await import('@/dev/impersonation/transport')).createHybridTransport(
                    m.sandboxTransport, httpTransport, m.setSandboxFallbackSession, undefined, {
                        // PWA-003: login real no servidor para quem nao e conta de exemplo
                        isSandboxIdentity: m.isSandboxIdentity,
                        checkSponsorSession: async () => (await httpTransport('GET', '/me')).status === 200,
                    })
                : m.sandboxTransport)
            : Promise.resolve(httpTransport)
    }
    return transportPromise
}

export async function request<T>(method: string, path: string, schema: z.ZodType<T> | null, body?: unknown): Promise<T> {
    let raw: RawResponse
    try {
        raw = await (await getTransport())(method, path, body)
    } catch {
        throw new SponsorApiError('network', 0)
    }
    if (raw.status < 200 || raw.status >= 300) throw toApiError(raw.status, raw.body)
    if (!schema) return undefined as T
    const envelope = z.object({ data: z.unknown(), translations: z.unknown().optional() }).passthrough().parse(raw.body)
    // PWA-020 D-04: traducoes do conteudo vem ao lado de `data`; formato errado e ignorado (a tela fica no original)
    if (envelope.translations !== undefined) {
        const translations = sponsorTranslationsSchema.safeParse(envelope.translations)
        if (translations.success) useTranslationsStore.getState().add(translations.data)
    }
    return schema.parse(envelope.data)
}

const enc = encodeURIComponent

export const sponsorApi = {
    // Acesso
    bootstrap: () => request('GET', '/bootstrap', sponsorBootstrapSchema),
    requestCode: (input: SponsorOtpRequestInput) => request('POST', '/auth/otp/request', sponsorOtpRequestResponseSchema, input),
    verifyCode: (challengeId: string, code: string) =>
        request('POST', '/auth/otp/verify', sponsorOtpVerifyResponseSchema, { challengeId, code }),
    redeemLink: (token: string) => request('POST', '/auth/link/redeem', sponsorOtpRequestResponseSchema, { token }),
    logout: () => request('POST', '/auth/logout', null),
    login: (input: SponsorLoginInput) => request('POST', '/auth/login', sponsorLoginResponseSchema, input),
    signupStart: (input: SponsorSignupStartInput) => request('POST', '/auth/signup/start', sponsorVerificationChallengeSchema, input),
    signupVerify: (challengeId: string, code: string) =>
        request('POST', '/auth/signup/verify', sponsorSignupVerifiedSchema, { challengeId, code }),
    signupComplete: (input: SponsorSignupCompleteInput) => request('POST', '/auth/signup/complete', sponsorLoginResponseSchema, input),
    passwordForgot: (input: SponsorPasswordForgotInput) => request('POST', '/auth/password/forgot', sponsorVerificationChallengeSchema, input),
    passwordVerify: (challengeId: string, code: string) =>
        request('POST', '/auth/password/verify', sponsorPasswordResetTokenSchema, { challengeId, code }),
    passwordReset: (resetToken: string, password: string) =>
        request('POST', '/auth/password/reset', null, { resetToken, password }),
    logoutAll: () => request('POST', '/auth/logout-all', null),

    // Padrinho
    me: () => request('GET', '/me', sponsorMeSchema),
    preferences: () => request('GET', '/me/preferences', sponsorPreferencesViewSchema),
    updatePreferences: (input: SponsorPreferencesInput) => request('PATCH', '/me/preferences', sponsorPreferencesViewSchema, input),
    account: () => request('GET', '/account', sponsorAccountSchema),
    // PWA-007: o padrinho atualiza os proprios dados (e-mail/celular com codigo no contato novo)
    updateProfile: (input: SponsorProfileUpdate) => request('PATCH', '/me/profile', sponsorAccountSchema, input),
    startContactChange: (input: SponsorContactChangeStartInput) =>
        request('POST', '/me/contact/start', sponsorVerificationChallengeSchema, input),
    verifyContactChange: (challengeId: string, code: string) =>
        request('POST', '/me/contact/verify', sponsorAccountSchema, { challengeId, code }),
    // PWA-031: endereco pelo CEP e alterar a senha com a atual
    postalCode: (cep: string) => request('GET', `/me/postal-code/${encodeURIComponent(cep.replace(/\D/g, ''))}`, sponsorPostalCodeLookupSchema),
    changePassword: (input: SponsorPasswordChangeInput) => request('POST', '/auth/password/change', null, input),
    requestPrivacy: (type: 'export' | 'delete') => request('POST', '/privacy/requests', sponsorPrivacyRequestResponseSchema, { type }),

    // Criancas
    children: () => request('GET', '/children', z.array(sponsorChildViewSchema)),
    child: (childRef: string) => request('GET', `/children/${enc(childRef)}`, sponsorChildDetailSchema),
    // PWA-027: linha do tempo do apadrinhamento (20 por pagina) e rituais ativos hoje (Inicio)
    timeline: (childRef: string, cursor?: string) =>
        request('GET', `/children/${enc(childRef)}/timeline${querystring({ cursor })}`, sponsorTimelinePageSchema),
    rituals: () => request('GET', '/rituals', sponsorRitualListSchema),

    // Inicio · Comunicados · Mensagens · Ajuda
    notices: () => request('GET', '/notices', sponsorNoticeListSchema),
    // PWA-017: Novidades do Inicio (5 por pagina)
    updates: (cursor?: string) => request('GET', `/updates${querystring({ cursor })}`, sponsorUpdatesPageSchema),
    markNoticesRead: () => request('POST', '/notices/read-all', null),
    threads: () => request('GET', '/threads', z.array(sponsorThreadSummarySchema)),
    thread: (threadId: string) => request('GET', `/threads/${enc(threadId)}`, sponsorThreadDetailSchema),
    markThreadRead: (threadId: string) => request('POST', `/threads/${enc(threadId)}/read`, null),
    // PWA-018: botao "Transcrever" num audio da conversa (o do padrinho ou o da crianca)
    transcribeAudio: (threadId: string, attachmentId: string) =>
        request('POST', `/threads/${enc(threadId)}/attachments/${enc(attachmentId)}/transcription`, sponsorTranscriptionResultSchema),
    // PWA-020 D-07: "Traduzir" uma mensagem recebida ou a transcricao de um audio da crianca (a traducao vem no envelope)
    translate: (threadId: string, input: SponsorTranslateRequest) =>
        request('POST', `/threads/${enc(threadId)}/translation`, z.object({ translated: z.boolean() }).strict(), input),
    // PWA-013: com foto ou audio, um pedido so em multipart (campos body, photo, audio); so texto continua em JSON
    sendMessage: (threadId: string, body: string, attachments: SponsorOutgoingAttachments = {}) =>
        request('POST', `/threads/${enc(threadId)}/messages`, sponsorThreadMessageSchema, messagePayload(body, attachments, i18next.language)),
    help: () => request('GET', '/help', sponsorHelpSchema),

    // Doacao, pagamento, visita, impacto e campanhas
    donations: () => request('GET', '/donations', sponsorDonationsSchema),
    updateSubscription: (amountCents: number) =>
        request('PATCH', '/donations/subscription', sponsorDonationsSchema, { amountCents }),
    updateCard: (paymentMethodToken: string) =>
        request('PUT', '/donations/subscription/card', sponsorDonationsSchema, { paymentMethodToken }),
    receipt: (year: number) => request('GET', `/donations/receipts/${year}`, sponsorReceiptSchema),
    donationCatalog: () => request('GET', '/donation-products', sponsorDonationCatalogSchema),
    savedCards: () => request('GET', '/payment-methods', z.array(sponsorSavedCardSchema)),
    checkout: (input: SponsorCheckoutInput) => request('POST', '/donations/checkout', sponsorCheckoutResultSchema, input),
    requestVisit: (input: SponsorVisitRequestInput) => request('POST', '/visits', sponsorVisitRequestResponseSchema, input),
    impact: () => request('GET', '/impact', sponsorImpactSchema),
    // PWA-028: eventos Live!Concerts acontecendo hoje
    events: () => request('GET', '/events', sponsorEventsSchema),

    // PWA-014: avisos neste aparelho (so com a sessao do proprio padrinho)
    pushConfig: () => request('GET', '/push/config', sponsorPushConfigSchema),
    pushSubscribe: (input: SponsorPushSubscriptionInput) => request('PUT', '/push/subscriptions', null, input),
    pushUnsubscribe: (endpoint: string) => request('DELETE', '/push/subscriptions', null, { endpoint }),
    pushTest: (endpoint: string) => request('POST', '/push/test', null, { endpoint }),

    // PWA-015: apadrinhar uma nova crianca (catalogo, reserva, pagamento simulado)
    catalog: (query: SponsorCatalogQuery) => request('GET', `/catalog${querystring(query)}`, sponsorCatalogPageSchema),
    catalogChild: (id: string) => request('GET', `/catalog/${enc(id)}`, sponsorCatalogChildSchema),
    reserveChild: (id: string) => request('POST', `/catalog/${enc(id)}/reservation`, sponsorReservationSchema, {}),
    releaseChild: (id: string) => request('DELETE', `/catalog/${enc(id)}/reservation`, null),
    adopt: (input: SponsorshipCheckout) => request('POST', '/sponsorships', sponsorshipResultSchema, input),

    // PWA-022: provedor de cartao (simulado, Braspag e, no sandbox, os cartoes de teste da Braspag)
    paymentsConfig: () => request('GET', '/payments/config', paymentsConfigSchema),
    /** FR-005: AccessToken do Silent Order Post (um por cartao). */
    sopToken: () => request('POST', '/payments/sop-token', sopTokenSchema, {}),
    // PWA-019: gerenciar o apadrinhamento feito pelo app
    appSponsorship: (protocol: string) => request('GET', `/app-sponsorships/${enc(protocol)}`, sponsorAppSponsorshipDetailSchema),
    changeAppSponsorshipCard: (protocol: string, input: SponsorAppCardChange) =>
        request('PUT', `/app-sponsorships/${enc(protocol)}/card`, sponsorAppCardChangeResultSchema, input),
    changeAppSponsorshipPlan: (protocol: string, input: SponsorAppPlanChange) =>
        request('PATCH', `/app-sponsorships/${enc(protocol)}`, sponsorAppSponsorshipDetailSchema, input),
    cancelAppSponsorship: (protocol: string, input: SponsorAppCancel) =>
        request('POST', `/app-sponsorships/${enc(protocol)}/cancel`, sponsorAppSponsorshipDetailSchema, input),
}

/** Parametros de busca sem os vazios. */
function querystring(params: Record<string, string | undefined>): string {
    const entries = Object.entries(params).filter((e): e is [string, string] => Boolean(e[1]))
    return entries.length ? `?${new URLSearchParams(entries).toString()}` : ''
}
